import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { CrmService } from '../../crm/services/crm.service';
import { catchError, map, switchMap, withLatestFrom } from 'rxjs/operators';
import { of } from 'rxjs';
import * as fromCompanyActions from '../actions/company.actions';
import { Company } from '../../crm/models/company.model';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { NotificationService } from '@fc-core/services/notification.service';
import { getCompanyList } from '../selectors';
import { select, Store } from '@ngrx/store';
import { HttpErrorResponse } from '@angular/common/http';
import { getErrorMessage } from '@fc-core/services/notification.utils';

@Injectable()
export class CompanyEffects {
  constructor(
    private actions$: Actions,
    private crmService: CrmService,
    private dialog: MatDialog,
    private router: Router,
    private notifications: NotificationService,
    private store: Store,
  ) {}

  addCompany$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromCompanyActions.addCompany),
      switchMap((action) => {
        return this.crmService.addCompany(action.company).pipe(
          map((company: Company) => {
            if (!action.action) this.dialog.closeAll();
            this.notifications.showSuccess('Company successfully added');
            if (action.action !== 'idle') this.navigateToCompany(company);
            return fromCompanyActions.addCompanySuccess({ company });
          }),
          catchError((err) => {
            const errorMessage = getErrorMessage(err);
            this.notifications.showError(errorMessage);
            return of(fromCompanyActions.companyError({ err }));
          }),
        );
      }),
    ),
  );

  updateCompany$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromCompanyActions.updateCompany),
      switchMap((action) => {
        return this.crmService.updateCompany(action.company, action.id).pipe(
          map((company: Company) => {
            this.dialog.closeAll();
            this.notifications.showSuccess('Company successfully updated');
            return fromCompanyActions.updateCompanySuccess({
              company: {
                id: company.id,
                changes: company,
              },
            });
          }),
          catchError((err) => {
            const errorMessage = getErrorMessage(err);
            this.notifications.showError(errorMessage);
            return of(fromCompanyActions.companyError({ err }));
          }),
        );
      }),
    ),
  );

  getCompanyList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromCompanyActions.loadCompany),
      withLatestFrom(this.store.pipe(select(getCompanyList))),
      switchMap(([_, companyList]) => {
        if (companyList.length !== 0) {
          return [fromCompanyActions.cancelLoadCompanies()];
        }
        return this.crmService.getCompanyList().pipe(
          map((data) =>
            fromCompanyActions.loadCompanySuccess({ company: data.list }),
          ),
          catchError((err: HttpErrorResponse) => {
            this.notifications.showError(err.error.toString());
            return of(fromCompanyActions.companyError({ err }));
          }),
        );
      }),
    ),
  );

  getMoreCompanyList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromCompanyActions.loadMoreCompany),
      switchMap(() => {
        return this.crmService.getCompanyList().pipe(
          map((data) =>
            fromCompanyActions.loadMoreCompanySuccess({ company: data.list }),
          ),
          catchError((err) => {
            this.notifications.showError(err.error.toString());
            return of(fromCompanyActions.companyError({ err }));
          }),
        );
      }),
    ),
  );

  deleteCompany$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromCompanyActions.deleteCompany),
      switchMap((action) => {
        return this.crmService.deleteCompany(action.id).pipe(
          map(() => {
            this.notifications.showSuccess('Company successfully deleted');
            this.router.navigate(['/crm/']);
            return fromCompanyActions.deleteCompanySuccess({
              id: action.id,
            });
          }),
          catchError((err) => {
            this.notifications.showError(err.error.toString());
            return of(fromCompanyActions.companyError({ err }));
          }),
        );
      }),
    ),
  );

  navigateToCompany(company: Company): void {
    this.router.navigate(['/crm/main/companies/' + company.id]);
  }
}
